/*
 * Copyright © 2022 EPO: European Patent Office.
 * ALL RIGHTS RESERVED
 */

import React, { useContext } from 'react';
import { Trans } from 'react-i18next';
import { Box, FormControlLabel, makeStyles, RadioGroup, Typography } from '@material-ui/core';
import CustomRadio from 'components/CustomRadio';
import { Alert } from '@material-ui/lab';
import { schemaRequiresTrueValue } from '../rjsf/utils';
import { SubmissionContext } from '../context/SubmissionContextProvider';
import { uiSchemaProperties } from '../../../shared/enums/uiSchemaProperties';
import Tooltip from '../../common/Tooltip';

const useStyles = makeStyles(theme => ({
  radioGroup: {
    rowGap: '.5rem',
  },
  radioOptionContainer: {
    display: 'flex',
  },
  alertInfoContainer: {
    marginTop: '1rem',
    backgroundColor: theme.palette.elementBackground,
  },
  tooltipLabel: {
    marginTop: '0.32rem',
  },
}));

export default function RadioWidget(props) {
  const {
    options,
    schema,
    id,
    value,
    disabled,
    readonly,
    label,
    autofocus,
    onChange,
    onBlur,
    onFocus,
    uiSchema,
    registry,
    hideTitle,
  } = props;

  const classes = useStyles();

  const tooltip = schema?.config?.tooltip ?? uiSchema?.[uiSchemaProperties.UI_TOOLTIP];
  const variantTitle = uiSchema?.[uiSchemaProperties.UI_VARIANT_TITLE];
  const infoMessage = uiSchema[uiSchemaProperties.UI_INFO_MESSAGE];
  const { showInfoMessage = false } = uiSchema;

  const { shouldApplyChange = () => true } = props;

  const required = schemaRequiresTrueValue(schema);
  const { enumDisabled } = options;
  const { enumTooltips } = schema;
  const { addFieldToClear } = useContext(SubmissionContext);

  const _onBlur = ({ target: { value: _value } }) => onBlur(id, _value);
  const _onFocus = ({ target: { value: _value } }) => onFocus(id, _value);

  const _onChange = ({ target: { value: _value } }) => {
    if (!shouldApplyChange(_value)) {
      return;
    }

    if (uiSchema?.['ui:options']?.triggerPostProcess === true || schema?.config?.triggerPostProcess === true) {
      registry.triggerPostProcess(id, _value);
    }

    onChange(_value);

    const resetsFieldIds = schema?.config?.resetsFieldIds;
    const addPrefix = schema?.config?.addResetIdPrefix ?? true;
    if (resetsFieldIds) {
      resetsFieldIds.forEach((resetsFieldId) => {
        const prefix = id.substring(0, id.lastIndexOf('_'));
        const fullId = `${prefix}_${resetsFieldId}`;
        setTimeout(() => addFieldToClear(addPrefix ? fullId : resetsFieldId), [5]);
      });
    }
  };

  return (
    <>
      {!hideTitle && (
        <Typography variant={variantTitle ?? 'h5'} component="span">
          {/* TODO: needs alignment. please align if we need to use strong or not. (backwards compatibility kept.) */}
          {variantTitle ? label : <strong>{label}</strong>}
          {tooltip && <Tooltip title={<Trans i18nKey={tooltip} />} />}
        </Typography>
      )}
      <RadioGroup className={classes.radioGroup} onBlur={_onBlur} onFocus={_onFocus} onChange={_onChange}>
        {options?.enumOptions &&
          options?.enumOptions?.map((option, index) => {
            const isDisabled = enumDisabled && enumDisabled.includes(option.value);
            return (
              option?.value && (
                <Box className={classes.radioOptionContainer} key={index}>
                  <FormControlLabel
                    key={option.value}
                    value={option.value}
                    disabled={disabled || readonly || isDisabled}
                    checked={value === option.value}
                    control={<CustomRadio color="primary" required={required} autoFocus={autofocus} />}
                    label={<Trans i18nKey={option.label} />}
                  />
                  {enumTooltips?.[index] && (
                    <Tooltip className={classes.tooltipLabel} title={<Trans i18nKey={enumTooltips?.[index]} />} />
                  )}
                </Box>
              )
            );
          })}
        <div id="info">
          {showInfoMessage && (
            <Alert id="alert-info" variant="outlined" severity="info" className={classes.alertInfoContainer}>
              <Trans i18nKey={infoMessage} />
            </Alert>
          )}
        </div>
      </RadioGroup>
    </>
  );
}
